import type { JSX } from 'react';
import { useId } from 'react';
import { Input } from './ui';
import { useAmountPercentageToggle, type InputMode } from '../hooks/useAmountPercentageToggle';

export interface EmployerPensionInputProps {
  /**
   * Current percentage value (e.g., 3 for 3%)
   */
  percentageValue: number;

  /**
   * Current amount value (e.g., 3000 for £3000)
   */
  amountValue: number;

  /**
   * Which mode is currently active ('percentage' or 'amount')
   */
  activeMode: InputMode;

  /**
   * Change handler - receives value and type
   * Parent should update the appropriate value based on type
   */
  onChange: (value: number, type: InputMode) => void;

  /**
   * Optional label (defaults to "Employer Pension Contributions")
   */
  label?: string;
}

/**
 * EmployerPensionInput component - handles employer pension with independent percentage/amount toggle
 *
 * Features:
 * - Switch between amount (£) and percentage (%) input
 * - NO automatic conversion - values are independent
 * - Only the active value is sent to backend
 *
 * @example
 * ```tsx
 * <EmployerPensionInput
 *   percentageValue={3}
 *   amountValue={5000}
 *   activeMode="percentage"
 *   onChange={(value, type) => {
 *     if (type === 'percentage') {
 *       setEmployerPensionPercentage(value);
 *     } else {
 *       setEmployerPensionAmount(value);
 *     }
 *     setEmployerPensionType(type);
 *   }}
 * />
 * ```
 */
export function EmployerPensionInput({
  percentageValue,
  amountValue,
  activeMode,
  onChange,
  label = 'Employer Pension',
}: EmployerPensionInputProps): JSX.Element {
  const inputId = useId();

  const { inputMode, displayValue, handleInputChange, handleModeToggle } = useAmountPercentageToggle({
    percentageValue,
    amountValue,
    activeMode,
    onChange,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={handleModeToggle}
          className="text-sm text-primary hover:opacity-80 transition-opacity font-medium"
          aria-label={`Switch to ${inputMode === 'amount' ? 'percentage' : 'amount'} mode`}
        >
          Switch to {inputMode === 'amount' ? '%' : '£'}
        </button>
      </div>
      <Input
        id={inputId}
        type="number"
        value={displayValue}
        onChange={handleInputChange}
        prefix={inputMode === 'amount' ? '£' : undefined}
        suffix={inputMode === 'percentage' ? '%' : undefined}
        helperText="Additional pension contributions from your employer (affects calculations for £260k+ earners)"
        fullWidth
        selectAllOnFocus
      />
    </div>
  );
}
